import {
  getCommunicationsDetails,
  getCommunicationsDetailsV2,
  isResponseError
} from "@coxauto-ui/cxm/communications/api";
import { CenteredSpinner } from "@coxauto-ui/core-components";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import parse from "html-react-parser";
import { ConversationContext, ConversationDetailsContext } from "../context";
import {
  conversationsDetailTestId,
  itemDetailsMessageDetailSentDateId,
  itemDetailsMessageDetailSentFromId,
  itemDetailsMessageDetailSentSubjectId,
  itemDetailsMessageDetailSentTextId,
  itemDetailsMessageDetailSentToId
} from "../testIds";
// eslint-disable-next-line
import { TagManager } from "@coxauto-ui/core-third-party-ga4";
import {
  ErrorMessages,
  convertUrlsToLinks,
  getDisplayableDate
} from "@coxauto-ui/cxm/communications/util";
import ItemAttachment from "./item-attachment";
import { ItemDetailsBackButton } from "./item-details-back-button";
import { useCxmFlags } from "@coxauto-ui/core/third-party/launch-darkly";
import { ErrorAlert } from "../components/error-alert";
import type { ErrorDetails } from "../components/error-alert";
import { extractErrorDetails } from "../utils/extract-error-details";
import { ConversationTypeEnum } from "@coxauto-ui/cxm/communications/interfaces";

const StyledMessageDetails = styled.div`
  padding: 16px;
  flex: 1;
  overflow-y: auto;
  overflow-wrap: anywhere;
`;

const StyledMessageDetailsLoaded = styled.div``;

const StyledSeparator = styled.hr`
  display: block;
  margin-top: 6px;
  margin-bottom: 6px;
  border-top: 1px solid #e3e3e3;
`;

const StyledNormalText = styled.span`
  font-weight: normal;
`;

const StyledItemAttachmentContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  column-gap: 25px;
  row-gap: 5px;
`;

export function MessageItemDetails() {
  const { customerId, customerSystem, activeListTab, env, vinDealerId } =
    useContext(ConversationContext);
  const { selectedConversationItem } = useContext(ConversationDetailsContext);
  const { "nx.cxm.use-message-details-v2": useMessageDetailsV2 } =
    useCxmFlags();

  const shouldUseV2 = useMessageDetailsV2 === true;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<ErrorDetails | undefined>(
    undefined
  );
  const [detailText, setDetailText] = useState<string | null>(null);

  const callGetAdditionalMessageDetail = useCallback(async () => {
    if (
      selectedConversationItem === null ||
      selectedConversationItem.key === null ||
      selectedConversationItem.key === undefined
    ) {
      return;
    }
    setLoading(true);
    setError(null);
    setErrorDetails(undefined);

    try {
      const communications = shouldUseV2
        ? await getCommunicationsDetailsV2(
            {
              messageDetailId: selectedConversationItem?.key || 0,
              ccID: customerId,
              consumerRecordSystem: customerSystem
            },
            env
          )
        : await getCommunicationsDetails(
            {
              messageDetailId: selectedConversationItem?.key || 0,
              ccID: customerId,
              consumerRecordSystem: customerSystem
            },
            env
          );

      if (isResponseError(communications)) {
        setError(ErrorMessages.MESSAGE_DETAILS_UNAVAILABLE);
        setErrorDetails({
          ...extractErrorDetails(communications),
          dealerId: vinDealerId,
          consumerId: customerId
        });
      } else {
        setDetailText(communications);
      }
    } catch (_err: unknown) {
      // Catch any unexpected errors (promise rejections, etc.)
      setError(ErrorMessages.MESSAGE_DETAILS_UNAVAILABLE);
      setErrorDetails({
        ...extractErrorDetails(_err),
        dealerId: vinDealerId,
        consumerId: customerId
      });
    } finally {
      setLoading(false);
    }
  }, [
    selectedConversationItem,
    customerId,
    customerSystem,
    env,
    shouldUseV2,
    vinDealerId
  ]);

  useEffect(() => {
    callGetAdditionalMessageDetail();
  }, [callGetAdditionalMessageDetail]);

  useEffect(() => {
    TagManager.event({
      event: "productEvent",
      eventName: "Message Details Viewed",
      eventProperties: {
        packagedBusinessCapability: "OCC Inbox",
        commonCustomerId: customerId,
        communicationChannel: activeListTab,
        replyContext: undefined,
        errorMessage: undefined
      }
    });
  }, [activeListTab, customerId]);

  const formattedSenderName = useMemo(() => {
    return selectedConversationItem?.senderName &&
      selectedConversationItem?.senderContactInfo
      ? selectedConversationItem?.senderName +
          " " +
          "(" +
          selectedConversationItem?.senderContactInfo +
          ")"
      : selectedConversationItem?.senderContactInfo
      ? "(" + selectedConversationItem?.senderContactInfo + ")"
      : "";
  }, [
    selectedConversationItem?.senderContactInfo,
    selectedConversationItem?.senderName
  ]);

  const formattedRecipientName = useMemo(() => {
    return selectedConversationItem?.recipientName &&
      selectedConversationItem?.recipientContactInfo
      ? selectedConversationItem?.recipientName +
          " " +
          "(" +
          selectedConversationItem?.recipientContactInfo +
          ")"
      : selectedConversationItem?.recipientContactInfo
      ? "(" + selectedConversationItem?.recipientContactInfo + ")"
      : "";
  }, [
    selectedConversationItem?.recipientContactInfo,
    selectedConversationItem?.recipientName
  ]);

  return (
    <StyledMessageDetails
      data-testid={conversationsDetailTestId}
      id="conversation-message-details"
    >
      {loading && (
        <CenteredSpinner id="conversation-message-details-loading-indicator" />
      )}
      {!loading && (
        <>
          <ItemDetailsBackButton />
          {error && (
            <ErrorAlert
              details={errorDetails}
              message={error}
              testId="message-details-error"
            />
          )}
        </>
      )}
      {!error && !loading && (
        <StyledMessageDetailsLoaded>
          <StyledSeparator />
          <b>From: </b>
          <StyledNormalText
            data-testid={itemDetailsMessageDetailSentFromId}
            id={itemDetailsMessageDetailSentFromId}
          >
            {formattedSenderName}
          </StyledNormalText>
          <br />
          <b>To: </b>
          <StyledNormalText
            data-testid={itemDetailsMessageDetailSentToId}
            id={itemDetailsMessageDetailSentToId}
          >
            {formattedRecipientName}
          </StyledNormalText>
          <br />
          {selectedConversationItem?.ccAddresses &&
            selectedConversationItem.ccAddresses.length > 0 && (
              <>
                <b>CC: </b>
                <StyledNormalText data-testid="item-details-message-detail-sent-cc">
                  {selectedConversationItem.ccAddresses.join(", ")}
                </StyledNormalText>
                <br />
              </>
            )}
          <b>Date: </b>
          <StyledNormalText
            data-testid={itemDetailsMessageDetailSentDateId}
            id={itemDetailsMessageDetailSentDateId}
          >
            {selectedConversationItem != null
              ? getDisplayableDate(new Date(selectedConversationItem.date))
              : ""}
          </StyledNormalText>
          <br />
          <br />
          {selectedConversationItem?.subject ? (
            <b
              data-testid={itemDetailsMessageDetailSentSubjectId}
              id={itemDetailsMessageDetailSentSubjectId}
            >
              Subject: {selectedConversationItem?.subject}
            </b>
          ) : null}
          <StyledSeparator />
          {selectedConversationItem?.attachments &&
            selectedConversationItem.attachments.length > 0 && (
              <>
                <StyledItemAttachmentContainer>
                  {selectedConversationItem.attachments.map(attachment => (
                    <div key={attachment.attachmentId}>
                      <ItemAttachment
                        attachmentId={attachment.attachmentId}
                        attachmentName={attachment.fileName}
                        attachmentType={attachment.type}
                        attachmentUrl={attachment.url}
                        coxAutoMessageId={selectedConversationItem.key}
                        date={selectedConversationItem.date}
                        simple={true}
                      />
                    </div>
                  ))}
                </StyledItemAttachmentContainer>
                <StyledSeparator />
              </>
            )}
          <StyledNormalText
            data-testid={itemDetailsMessageDetailSentTextId}
            id={itemDetailsMessageDetailSentTextId}
          >
            {parse(
              selectedConversationItem?.type === ConversationTypeEnum.TEXT
                ? convertUrlsToLinks(detailText ?? "")
                : detailText ?? ""
            )}
          </StyledNormalText>
        </StyledMessageDetailsLoaded>
      )}
    </StyledMessageDetails>
  );
}

export default MessageItemDetails;
